import apiClient from '../config/axiosConfig';
import { API_BASE_URL } from '../utils/apiUtils';

const validateImageId = (imageId) => {
  if (!imageId || typeof imageId !== 'string' || imageId.trim() === '') {
    throw new Error('Image ID is required and must be a valid string');
  }

  const idRegex = /^[a-zA-Z0-9-_]+$/;
  if (!idRegex.test(imageId.trim())) {
    throw new Error('Image ID must contain only letters, numbers, hyphens, and underscores');
  }

  return imageId.trim();
};

const validateImageUrl = (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== 'string' || imageUrl.trim() === '') {
    throw new Error('Image URL is required and must be a valid string');
  }

  try {
    new URL(imageUrl.trim());
  } catch (error) {
    throw new Error('Invalid image URL format provided');
  }

  return imageUrl.trim();
};

const validateImageFile = (imageFile) => {
  if (!imageFile) {
    throw new Error('Image file is required');
  }

  if (!(imageFile instanceof File)) {
    throw new Error('Image must be a valid file');
  }

  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  if (!allowedTypes.includes(imageFile.type)) {
    throw new Error('Image must be in JPEG, PNG, or WebP format');
  }

  const maxSize = 10 * 1024 * 1024;
  if (imageFile.size > maxSize) {
    throw new Error('Image file size must be less than 10MB');
  }

  return imageFile;
};

const validateSearchQuery = (query) => {
  if (query !== null && query !== undefined) {
    if (typeof query !== 'string') {
      throw new Error('Search query must be a string');
    }

    const trimmedQuery = query.trim();
    if (trimmedQuery.length > 100) {
      throw new Error('Search query must be less than 100 characters');
    }

    return trimmedQuery;
  }
  return '';
};

const validatePage = (page) => {
  if (page !== null && page !== undefined) {
    const pageNum = parseInt(page, 10);
    if (isNaN(pageNum) || pageNum < 1) {
      throw new Error('Page must be a positive integer');
    }
    return pageNum;
  }
  return 1;
};

const validateLimit = (limit) => {
  if (limit !== null && limit !== undefined) {
    const limitNum = parseInt(limit, 10);
    if (isNaN(limitNum) || limitNum < 1 || limitNum > 50) {
      throw new Error('Limit must be a positive integer between 1 and 50');
    }
    return limitNum;
  }
  return 20;
};

const validateImageData = (imageData) => {
  if (imageData && typeof imageData === 'object') {
    const validImageData = {};

    if (imageData.title !== undefined) {
      if (typeof imageData.title !== 'string') {
        throw new Error('Image title must be a string');
      }
      validImageData.title = imageData.title.trim();
    }

    if (imageData.description !== undefined) {
      if (typeof imageData.description !== 'string') {
        throw new Error('Image description must be a string');
      }
      validImageData.description = imageData.description.trim();
    }

    if (imageData.tags !== undefined) {
      if (!Array.isArray(imageData.tags)) {
        throw new Error('Image tags must be an array');
      }
      validImageData.tags = imageData.tags.filter(tag => typeof tag === 'string').map(tag => tag.trim());
    }

    return validImageData;
  }
  return {};
};

const handleVirtualBackgroundError = (error, operation = 'Virtual background operation') => {
  if (error.response?.status === 401) {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    throw new Error('Your session has expired. Please log in again');
  }

  if (error.response?.status === 403) {
    throw new Error('You do not have permission to access virtual background features');
  }

  if (error.response?.status === 404) {
    throw new Error('Virtual background image not found or no longer available');
  }

  if (error.response?.status === 413) {
    throw new Error('Image file is too large. Please use a smaller image (max 10MB)');
  }

  if (error.response?.status === 415) {
    throw new Error('Unsupported image format. Please use JPEG, PNG, or WebP');
  }

  if (error.response?.status === 422) {
    const validationErrors = error.response?.data?.errors || [];
    const errorMessage = validationErrors.length > 0
      ? `Validation errors: ${validationErrors.join(', ')}`
      : 'Invalid virtual background data provided';
    throw new Error(errorMessage);
  }

  if (error.response?.status === 429) {
    throw new Error('Too many requests. Please wait a moment and try again.');
  }

  if (error.response?.status >= 500) {
    throw new Error('Virtual background service error. Please try again later.');
  }

  if (error.code === 'ERR_NETWORK') {
    throw new Error('Network error. Please check your connection and try again.');
  }

  if (error.code === 'ECONNABORTED') {
    throw new Error('Request timeout. Please try again.');
  }

  const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
  throw new Error(errorMessage);
};

export const fetchVirtualBackgroundImages = async (options = {}) => {
  try {
    const validQuery = validateSearchQuery(options.query);
    const validPage = validatePage(options.page);
    const validLimit = validateLimit(options.limit);

    const queryParams = new URLSearchParams();

    if (validQuery) {
      queryParams.append('query', validQuery);
    }

    queryParams.append('page', validPage);
    queryParams.append('limit', validLimit);

    const response = await apiClient.get(
      `${API_BASE_URL}/api/virtual-background/random-images?${queryParams.toString()}`
    );

    return response.data;
  } catch (error) {
    handleVirtualBackgroundError(error, 'Fetching virtual background images');
  }
};

export const uploadVirtualBackgroundImage = async (imageFile, imageData = {}) => {
  try {
    const validImageFile = validateImageFile(imageFile);
    const validImageData = validateImageData(imageData);

    const formData = new FormData();
    formData.append('image', validImageFile);

    if (validImageData.title) {
      formData.append('title', validImageData.title);
    }

    if (validImageData.description) {
      formData.append('description', validImageData.description);
    }

    if (validImageData.tags && validImageData.tags.length > 0) {
      formData.append('tags', JSON.stringify(validImageData.tags));
    }

    const response = await apiClient.post(
      `${API_BASE_URL}/api/virtual-background/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  } catch (error) {
    handleVirtualBackgroundError(error, 'Uploading virtual background image');
  }
};

export const getUserVirtualBackgroundImages = async (page = 1, limit = 20) => {
  try {
    const validPage = validatePage(page);
    const validLimit = validateLimit(limit);

    const response = await apiClient.get(
      `${API_BASE_URL}/api/virtual-background/user-images?page=${validPage}&limit=${validLimit}`
    );

    return response.data;
  } catch (error) {
    handleVirtualBackgroundError(error, 'Getting user virtual background images');
  }
};

export const deleteVirtualBackgroundImage = async (imageId) => {
  try {
    const validImageId = validateImageId(imageId);

    const response = await apiClient.delete(
      `${API_BASE_URL}/api/virtual-background/delete/${validImageId}`
    );

    return response.data;
  } catch (error) {
    handleVirtualBackgroundError(error, 'Deleting virtual background image');
  }
};

export const updateVirtualBackgroundImage = async (imageId, imageData = {}) => {
  try {
    const validImageId = validateImageId(imageId);
    const validImageData = validateImageData(imageData);

    const response = await apiClient.put(
      `${API_BASE_URL}/api/virtual-background/update/${validImageId}`,
      validImageData
    );

    return response.data;
  } catch (error) {
    handleVirtualBackgroundError(error, 'Updating virtual background image');
  }
};

export const searchVirtualBackgroundImages = async (query, page = 1, limit = 20) => {
  try {
    const validQuery = validateSearchQuery(query);
    const validPage = validatePage(page);
    const validLimit = validateLimit(limit);

    const response = await apiClient.get(
      `${API_BASE_URL}/api/virtual-background/search?query=${encodeURIComponent(validQuery)}&page=${validPage}&limit=${validLimit}`
    );

    return response.data;
  } catch (error) {
    handleVirtualBackgroundError(error, 'Searching virtual background images');
  }
};

export const getVirtualBackgroundImageById = async (imageId) => {
  try {
    const validImageId = validateImageId(imageId);

    const response = await apiClient.get(
      `${API_BASE_URL}/api/virtual-background/image/${validImageId}`
    );

    return response.data;
  } catch (error) {
    handleVirtualBackgroundError(error, 'Getting virtual background image by ID');
  }
};

export const getPopularVirtualBackgroundImages = async (page = 1, limit = 20) => {
  try {
    const validPage = validatePage(page);
    const validLimit = validateLimit(limit);

    const response = await apiClient.get(
      `${API_BASE_URL}/api/virtual-background/popular?page=${validPage}&limit=${validLimit}`
    );

    return response.data;
  } catch (error) {
    handleVirtualBackgroundError(error, 'Getting popular virtual background images');
  }
};
